/** Thin client for the Finversify API. Errors arrive as ApiError with a plain message to show. */

const TOKEN_KEY = "fv_session";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return memoryToken;
  }
}
let memoryToken: string | null = null;
export function setToken(token: string | null): void {
  memoryToken = token;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private mode: keep in memory only */
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly reasons: string[] = [],
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

function messageFrom(status: number, body: Record<string, unknown>): string {
  if (typeof body.message === "string") return body.message;
  if (body.message && typeof body.message === "object") {
    const inner = body.message as Record<string, unknown>;
    if (typeof inner.message === "string") return inner.message;
  }
  if (Array.isArray(body.issues) && body.issues.length) {
    return (body.issues as { message: string }[]).map((i) => i.message).join(". ");
  }
  if (Array.isArray(body.reasons) && body.reasons.length) return String(body.reasons[0]);
  if (status === 502) return "A partner service did not respond. Please try again in a minute.";
  return "Something went wrong. Please try again.";
}

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(path, {
    method: init.method ?? (init.body === undefined ? "GET" : "POST"),
    headers: {
      ...(init.body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  const text = await res.text();
  let body: Record<string, unknown> = {};
  try {
    body = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    body = {};
  }
  if (!res.ok) {
    // Nest wraps object messages: { message: {...}, error, statusCode } or puts fields at top level.
    const flat = body.message && typeof body.message === "object" ? { ...body, ...(body.message as Record<string, unknown>) } : body;
    if (res.status === 401 && flat.error === "NOT_SIGNED_IN") setToken(null);
    throw new ApiError(
      res.status,
      String(flat.error ?? `HTTP_${res.status}`),
      messageFrom(res.status, flat),
      Array.isArray(flat.reasons) ? (flat.reasons as string[]) : [],
      flat,
    );
  }
  return body as T;
}

// ───────── shapes returned by the API ─────────

export type Step = "PROFILE" | "KYC" | "AGREEMENTS" | "PLAN" | "DONE";

export interface Profile {
  fullName: string;
  email: string;
  clientType: "INDIVIDUAL" | "HUF" | "NON_INDIVIDUAL";
  accreditedInvestor: boolean;
  dateOfBirth: string;
  segregationDeclared: boolean;
  city: string;
  state: string;
}

export interface OnboardingState {
  clientId: string;
  mobile: string;
  step: Step;
  profile: Profile | null;
  kyc: {
    pan: string;
    status: string;
    statusCode: string;
    nextStep: "PROCEED" | "PROCEED_RECHECK" | "HOLD" | "FRESH_KYC" | "BLOCKED" | "INVALID_PAN" | "RETRY" | "MANUAL_REVIEW";
    canContinue: boolean;
    nameOnRecord: string;
    missingFields: string[];
    source: "KRA_FETCH" | "FRESH_EKYC";
  } | null;
  agreementsSigned: boolean;
}

export interface Agreement {
  type: string;
  version: string;
  title: string;
  body: string;
  signed: boolean;
}

export interface Plan {
  id: string;
  serviceId: string;
  days: 30 | 90 | 180 | 365;
  feePaise: number;
}

export interface Service {
  id: string;
  name: string;
  summary: string;
  highlights: string[];
  plans: Plan[];
}

export interface Catalogue {
  services: Service[];
  states: string[];
}

export interface Quote {
  periodStart: string;
  periodEnd: string;
  isRenewal: boolean;
  feePaise: number;
  gstPaise: number;
  totalPaise: number;
  allowed: boolean;
  codes: string[];
  reasons: string[];
}

export interface Order {
  orderId: string;
  keyId: string;
  sandbox: boolean;
  amountPaise: number;
  currency: string;
  description: string;
  prefill: { name: string; email: string; contact: string };
}

export interface Me {
  name: string | null;
  mobile: string;
  email: string | null;
  step: Step;
  subscriptions: Subscription[];
  invoices: { paymentId: string; invoiceNumber: string; paidAt: string; service: string | null; feePaise: number; gstPaise: number; totalPaise: number; pdfReady: boolean; refundRequired: boolean }[];
  signedDocuments: { type: string; version: string; signedAt: string }[];
}

export interface TelegramAccess {
  /** The channel's name in Telegram, e.g. "Commodity Trades". */
  channel: string;
  status: "INVITE" | "JOINED" | "PENDING" | "EXPIRED" | "REMOVED" | "NONE";
  inviteLink: string | null;
  inviteExpiresAt: string | null;
}

export interface Subscription {
  id: string;
  serviceId: string;
  service: string;
  planDays: number;
  periodStart: string;
  periodEnd: string;
  status: "ACTIVE" | "EXPIRED" | "CANCELLED";
  isRenewal: boolean;
  startsLater: boolean;
  canRenew: boolean;
  telegram: TelegramAccess;
}

export interface PaymentResult {
  subscription: { id: string; service: string; planDays: number; periodStart: string; periodEnd: string; isRenewal: boolean; status: string; telegram: TelegramAccess };
  invoiceNumber: string;
  paymentId: string;
  activated: boolean;
  reasons: string[];
}

/** Downloads the invoice PDF with the session token (a plain link cannot send it). */
export async function downloadInvoice(paymentId: string, invoiceNumber: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`/v1/me/payments/${paymentId}/invoice`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new ApiError(res.status, `HTTP_${res.status}`, "The invoice is not ready yet. Try again in a minute.");
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = `Finversify-Invoice-${invoiceNumber.replaceAll("/", "-")}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export interface ClientCall {
  callCode: string;
  service: string;
  exchange: string;
  instrument: string;
  side: "BUY" | "SELL";
  entry: { type: "FP" | "PR" | "CMP"; from: number; to: number };
  stopLoss: number;
  targets: number[];
  horizonDays: number | null;
  rationale: string;
  status: "ACTIVE" | "CLOSED";
  exitPrice: number | null;
  closeReason: "TARGET_HIT" | "STOPLOSS_HIT" | "EXIT_ADVISED" | "EXPIRED" | null;
  publishedAt: string;
  lastUpdatedAt: string;
  history: { at: string; kind: "PUBLISH" | "UPDATE" | "CLOSE"; stopLoss: number; targets: number[]; exitPrice: number | null; note: string | null }[];
}
